/**
 * JSON rendering components for ToolCallCard.
 */

import { Icon } from "@/components/atoms/Icon";
import { AnimatePresence, motion } from "framer-motion";
import { type CSSProperties, useState } from "react";

import { icons } from "@/objects/@icons";
import { Typography } from "@/components/ui/typography";

// ── Syntax-highlighted JSON renderer ──────────────────────────────────────────

export function ToolCallCardSyntaxJson({ data }: { data: unknown }) {
  const renderValue = (value: unknown, indent: number = 0): React.ReactNode => {
    const indentStr = "  ".repeat(indent);
    const nextIndent = indent + 1;
    const nextIndentStr = "  ".repeat(nextIndent);

    if (value === null) {
      return <span className="text-muted-foreground/80">null</span>;
    }

    if (typeof value === "boolean") {
      return <span className="text-muted-foreground/80">{String(value)}</span>;
    }

    if (typeof value === "number") {
      return <span className="text-muted-foreground">{value}</span>;
    }

    if (typeof value === "string") {
      return <span className="text-foreground">&quot;{value}&quot;</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground/80">[]</span>;
      }
      return (
        <>
          <span className="text-muted-foreground/80">[</span>
          {"\n"}
          {value.map((item, i) => (
            <span key={i}>
              {nextIndentStr}
              {renderValue(item, nextIndent)}
              {i < value.length - 1 && (
                <span className="text-muted-foreground/80">,</span>
              )}
              {"\n"}
            </span>
          ))}
          {indentStr}
          <span className="text-muted-foreground/80">]</span>
        </>
      );
    }

    if (typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) {
        return <span className="text-muted-foreground/80">{"{}"}</span>;
      }
      return (
        <>
          <span className="text-muted-foreground/80">{"{"}</span>
          {"\n"}
          {entries.map(([key, val], i) => (
            <span key={key}>
              {nextIndentStr}
              <span className="text-muted-foreground">&quot;{key}&quot;</span>
              <span className="text-muted-foreground/80">: </span>
              {renderValue(val, nextIndent)}
              {i < entries.length - 1 && (
                <span className="text-muted-foreground/80">,</span>
              )}
              {"\n"}
            </span>
          ))}
          {indentStr}
          <span className="text-muted-foreground/80">{"}"}</span>
        </>
      );
    }

    return String(value);
  };

  return (
    <pre className="font-mono text-[11px] [line-height:1.4] m-0 p-2 bg-accent rounded-[4px] overflow-auto whitespace-pre">
      {renderValue(data)}
    </pre>
  );
}

// ── Collapsible JSON section ──────────────────────────────────────────────────

export function ToolCallCardCollapsibleJson({
  label,
  data,
  color = "var(--muted-foreground)",
}: {
  label: string;
  data: Record<string, unknown> | string;
  /** Its label's colour, as CSS. */
  color?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isJson = typeof data !== "string";

  return (
    <div>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 cursor-pointer hover:opacity-[0.8]"
      >
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.15 }}
          style={{ display: "flex", fontSize: "8px" }}
        >
          <Icon
            ReactIcon={icons.chevronDown}
            style={{ transform: "rotate(-90deg)" }}
          />
        </motion.div>
        <Typography
          level="body-xs"
          className="font-mono text-[9px] [font-weight:600] text-(--json-label-color) uppercase [letter-spacing:0.05em]"
          style={{ "--json-label-color": color } as CSSProperties}
        >
          {label}
        </Typography>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ overflow: "hidden" }}
          >
            <div className="mt-1 rounded-[4px] overflow-hidden max-h-50 overflow-y-auto">
              {isJson ? (
                <ToolCallCardSyntaxJson data={data} />
              ) : (
                <div className="font-mono text-[11px] bg-accent text-foreground rounded-[4px] p-2 whitespace-pre-wrap [word-break:break-word]">
                  {data}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
